import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';
import { useLaundryProfile } from '@/context/LaundryProfileContext';
import Logo from './Logo';
import { navGroups } from './navConfig';

interface SidebarContentProps {
  onNavigate?: () => void;
  collapsed?: boolean;
}

export function SidebarContent({ onNavigate, collapsed = false }: SidebarContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { profile } = useLaundryProfile();

  const laundryName = profile?.name ?? user?.laundryName ?? t('app.name');

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-full flex-col bg-background-50">
      <header
        className={cn(
          'border-b border-background-200/70',
          collapsed ? 'px-2 pb-4 pt-6' : 'px-5 pb-5 pt-7',
        )}
      >
        {collapsed ? (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 items-center justify-center">
              <Logo variant="icon" className="h-9" />
            </div>
            <span
              className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary-100 text-primary-700"
              title={laundryName}
            >
              {profile?.logoUrl ? (
                <img src={profile.logoUrl} alt={laundryName} className="h-full w-full object-cover" />
              ) : (
                <i className="ri-store-2-line text-lg leading-none" aria-hidden="true" />
              )}
            </span>
          </div>
        ) : (
          <>
            <div className="flex h-24 items-center justify-center">
              <Logo variant="color" className="h-20" />
            </div>
            <div className="mt-5 flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 px-3.5 py-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary-100 text-primary-700">
                {profile?.logoUrl ? (
                  <img src={profile.logoUrl} alt={laundryName} className="h-full w-full object-cover" />
                ) : (
                  <i className="ri-store-2-line text-xl leading-none" aria-hidden="true" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground-950">{laundryName}</p>
                <p className="mt-0.5 truncate text-xs text-foreground-500">{t('app.dashboard')}</p>
              </div>
            </div>
          </>
        )}
      </header>

      <nav className={cn('flex-1 py-4', collapsed ? 'overflow-visible px-2' : 'overflow-y-auto px-3')}>
        {navGroups.map((group, groupIndex) => (
          <div
            key={group.key}
            className={cn(
              groupIndex > 0 &&
                (collapsed
                  ? 'mt-3 border-t border-background-200/70 pt-3'
                  : 'mt-6'),
            )}
          >
            {!collapsed ? (
              <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                {t(group.labelKey)}
              </p>
            ) : null}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.to === '/dashboard'}
                  onClick={onNavigate}
                  title={collapsed ? t(item.labelKey) : undefined}
                  aria-label={collapsed ? t(item.labelKey) : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex h-11 items-center rounded-md text-sm font-medium transition-all duration-200 ease-smooth',
                      collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                      isActive
                        ? 'bg-accent-100/90 font-semibold text-primary-800'
                        : 'text-foreground-700 hover:bg-background-50/70 hover:text-foreground-900',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? (
                        <span
                          className="absolute inset-y-2 start-0 w-1 rounded-full bg-primary-600"
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                        <i
                          className={cn(
                            item.icon,
                            'text-lg leading-none transition-colors duration-200 ease-smooth',
                            isActive ? 'text-primary-600' : 'text-foreground-500',
                          )}
                          aria-hidden="true"
                        />
                      </span>
                      {!collapsed ? <span className="flex-1 truncate">{t(item.labelKey)}</span> : null}
                      {collapsed ? (
                        <span
                          className="pointer-events-none absolute top-1/2 z-40 start-full ms-3 -translate-y-1/2 whitespace-nowrap rounded-md bg-foreground-950 px-2.5 py-1.5 text-xs font-medium text-background-50 opacity-0 shadow-pop transition-opacity duration-150 group-hover:opacity-100"
                          aria-hidden="true"
                        >
                          {t(item.labelKey)}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <footer className={cn('border-t border-background-200/70 py-4', collapsed ? 'px-2' : 'px-3')}>
        <button
          type="button"
          onClick={handleLogout}
          title={collapsed ? t('topbar.logout') : undefined}
          aria-label={collapsed ? t('topbar.logout') : undefined}
          className={cn(
            'flex h-11 w-full items-center rounded-md border border-rose-200 bg-rose-50/70 text-sm font-medium text-rose-600 transition-colors duration-200 ease-smooth hover:bg-rose-100 cursor-pointer',
            collapsed ? 'justify-center px-0' : 'gap-3 px-3',
          )}
        >
          <span className="flex h-6 w-6 items-center justify-center">
            <i className="ri-logout-box-r-line text-lg leading-none" aria-hidden="true" />
          </span>
          {!collapsed ? <span className="flex-1 truncate text-start">{t('topbar.logout')}</span> : null}
        </button>
        {collapsed ? (
          <div className="mt-3 flex justify-center">
            <span
              className="flex h-6 items-center gap-1.5 text-xs text-foreground-500"
              title={t('common.demoData')}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            </span>
          </div>
        ) : (
          <span className="mt-3 flex items-center justify-center gap-1.5 text-xs text-foreground-500">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            {t('common.demoData')}
          </span>
        )}
      </footer>
    </div>
  );
}

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function Sidebar({ collapsed = false, onToggleCollapse }: SidebarProps) {
  const { t } = useTranslation();

  return (
    <aside
      className={cn(
        'relative hidden shrink-0 border-e border-background-200/70 transition-[width] duration-300 ease-smooth lg:block',
        collapsed ? 'w-[84px]' : 'w-[300px]',
      )}
    >
      <div className="sticky top-0 h-screen">
        <SidebarContent collapsed={collapsed} />

        {onToggleCollapse ? (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? t('topbar.expandSidebar') : t('topbar.collapseSidebar')}
            title={collapsed ? t('topbar.expandSidebar') : t('topbar.collapseSidebar')}
            className="absolute top-9 -end-3.5 z-20 flex h-7 w-7 items-center justify-center rounded-full border border-background-200 bg-background-50 text-foreground-500 transition-colors duration-200 ease-smooth hover:bg-background-100 hover:text-foreground-900 cursor-pointer"
          >
            <i
              className={cn(
                collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line',
                'text-base leading-none rtl:rotate-180',
              )}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
    </aside>
  );
}